import { FormEvent, useCallback, useState } from 'react';

import { isEqual } from '../services';

import { ColumnProps } from './component';

export interface UseColumnMenuOutput {
  columns: ColumnProps[];
  columnsExpanded: boolean;
  filterExpanded: boolean;
  onToggleColumn: (id: number) => void;
  onReset: (event: FormEvent<HTMLFormElement>) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onColumnsClick: () => void;
  onFilterExpandChange: (value: boolean) => void;
}

export enum Fields {
  MENUBUTTON = 'menuButton',
}

export const useColumnMenu = (
  receivedColumns: ColumnProps[],
  onColumnsSubmit: (columns: ColumnProps[]) => void,
  onCloseMenu: Function | undefined,
): UseColumnMenuOutput => {
  const [columns, setColumns] = useState(receivedColumns);
  const [columnsExpanded, setColumnsExpanded] = useState(false);
  const [filterExpanded, setFilterExpanded] = useState(false);

  const onToggleColumn = useCallback(
    (index: number) => {
      setColumns((currentColumns) =>
        currentColumns.map((column: ColumnProps, columnIndex: number) => {
          return columnIndex === index
            ? { ...column, show: !column.show }
            : column;
        }),
      );
    },
    [setColumns],
  );

  const onReset = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setColumns((currentColumns) =>
        currentColumns.map((column) => {
          return {
            ...column,
            show: true,
          };
        }),
      );
    },
    [setColumns],
  );
  const onSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!isEqual(columns, receivedColumns)) {
        onColumnsSubmit(columns);
        if (onCloseMenu) {
          onCloseMenu();
        }
      }
    },
    [columns, onColumnsSubmit, onCloseMenu, receivedColumns],
  );

  const onColumnsClick = useCallback(() => {
    setColumnsExpanded((currentColumnsExpanded) => {
      const returnedValue = !currentColumnsExpanded;
      if (returnedValue) {
        setFilterExpanded(false);
      }

      return returnedValue;
    });
  }, [setColumnsExpanded, setFilterExpanded]);

  const onFilterExpandChange = useCallback(
    (value: boolean) => {
      setFilterExpanded(value);
      if (value) {
        setColumnsExpanded(false);
      }
    },
    [setColumnsExpanded, setFilterExpanded],
  );

  return {
    columns,
    columnsExpanded,
    filterExpanded,
    onToggleColumn,
    onReset,
    onSubmit,
    onColumnsClick,
    onFilterExpandChange,
  };
};
